import type {
  AnnotationRange,
  AnnotationRepairReason,
} from "./annotations.js";

export type AnnotationRepairReportReason = AnnotationRepairReason | "order-normalized";

export interface AnnotationRepairReportEntry {
  readonly order: number;
  readonly original: AnnotationRange;
  readonly repaired: AnnotationRange | null;
  readonly outcome: "repaired" | "dropped";
  readonly reasons: readonly AnnotationRepairReportReason[];
}

export interface AnnotationRepairReport {
  readonly accepted: readonly AnnotationRange[];
  readonly repairs: readonly AnnotationRepairReportEntry[];
}

export interface AnnotationRepairTrayActions {
  onUse(accepted: readonly AnnotationRange[]): void;
  onDiscard(retained: readonly AnnotationRange[]): void;
}

export interface AnnotationRepairTray {
  readonly element: HTMLElement;
  destroy(): void;
}

type TrayState = "pending" | "invoking" | "decided" | "destroyed";

const documentCounters = new WeakMap<Document, number>();
const ORDER_NORMALIZED_REASONS = Object.freeze([
  "order-normalized" as const,
]);

const freezeRange = (range: AnnotationRange): AnnotationRange => Object.freeze({
  id: range.id,
  start: range.start,
  end: range.end,
});

const rangesMatch = (
  left: readonly AnnotationRange[],
  right: readonly AnnotationRange[],
): boolean => left.length === right.length && left.every((range, index) => {
  const candidate = right[index];
  return candidate !== undefined
    && range.id === candidate.id
    && range.start === candidate.start
    && range.end === candidate.end;
});

export const withOrderNormalizationReport = (
  original: readonly AnnotationRange[],
  result: AnnotationRepairReport,
): AnnotationRepairReport => {
  const originalSnapshot = Object.freeze(original.map(freezeRange));
  const accepted = Object.freeze(result.accepted.map(freezeRange));
  const ordinaryRepairs = Object.freeze(result.repairs.map((repair) => Object.freeze({
    order: repair.order,
    original: freezeRange(repair.original),
    repaired: repair.repaired === null ? null : freezeRange(repair.repaired),
    outcome: repair.outcome,
    reasons: Object.freeze([...repair.reasons]),
  })));
  const repairedIds = new Set(ordinaryRepairs.map((repair) => repair.original.id));
  const originalUnrepaired = originalSnapshot.filter((range) => !repairedIds.has(range.id));
  const acceptedUnrepaired = accepted.filter((range) => !repairedIds.has(range.id));
  const acceptedIndexes = new Map(
    acceptedUnrepaired.map((range, index) => [range.id, index]),
  );
  const orderAffectedIds = new Set(originalUnrepaired.flatMap((range, index) =>
    acceptedIndexes.get(range.id) === index ? [] : [range.id]
  ));
  const orderRepairs = originalSnapshot.flatMap((range, originalIndex) => {
    if (!orderAffectedIds.has(range.id)) return [];
    const repairedIndex = accepted.findIndex((candidate) => rangesMatch([range], [candidate]));
    const repaired = repairedIndex < 0 ? null : accepted[repairedIndex] ?? null;
    return [Object.freeze({
      order: repairedIndex < 0 ? originalIndex : repairedIndex,
      original: range,
      repaired,
      outcome: repaired === null ? "dropped" as const : "repaired" as const,
      reasons: ORDER_NORMALIZED_REASONS,
    })];
  });
  return Object.freeze({
    accepted,
    repairs: Object.freeze([...ordinaryRepairs, ...orderRepairs]),
  });
};

const appendTextElement = (
  document: Document,
  parent: HTMLElement,
  tagName: string,
  text: string,
): HTMLElement => {
  const element = document.createElement(tagName);
  element.textContent = text;
  parent.append(element);
  return element;
};

const nextAccessibleIds = (document: Document): { title: string; status: string } => {
  let counter = documentCounters.get(document) ?? 0;
  let title: string;
  let status: string;
  do {
    counter += 1;
    title = `annotation-repair-title-${counter}`;
    status = `annotation-repair-status-${counter}`;
  } while (Boolean(document.getElementById?.(title)) || Boolean(document.getElementById?.(status)));
  documentCounters.set(document, counter);
  return { title, status };
};

/**
 * Renders pending repair diagnostics. No caller state changes until one of the
 * two explicit actions succeeds.
 */
export const renderAnnotationRepairTray = (
  host: HTMLElement,
  result: AnnotationRepairReport,
  actions: AnnotationRepairTrayActions,
): AnnotationRepairTray | null => {
  if (result.repairs.length === 0) return null;

  const document = host.ownerDocument;
  const ids = nextAccessibleIds(document);
  const affectedIds = new Set(result.repairs.map((repair) => repair.original.id));
  const retained = Object.freeze(
    result.accepted.filter((range) => !affectedIds.has(range.id)),
  );

  const aside = document.createElement("aside");
  aside.setAttribute("role", "region");
  aside.setAttribute("aria-labelledby", ids.title);
  aside.setAttribute("aria-describedby", ids.status);
  aside.tabIndex = -1;

  const title = appendTextElement(document, aside, "h2", "Annotation repairs");
  title.setAttribute("id", ids.title);

  const status = document.createElement("p");
  status.setAttribute("id", ids.status);
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-atomic", "true");
  aside.append(status);

  const list = document.createElement("ul");
  for (const repair of result.repairs) {
    appendTextElement(
      document,
      list,
      "li",
      `${repair.original.id}: ${repair.reasons.join(", ")} (${repair.outcome})`,
    );
  }
  aside.append(list);

  const use = appendTextElement(
    document,
    aside,
    "button",
    "Use repaired annotations",
  ) as HTMLButtonElement;
  use.type = "button";

  const discard = appendTextElement(
    document,
    aside,
    "button",
    "Discard affected annotations",
  ) as HTMLButtonElement;
  discard.type = "button";

  let state: TrayState = "pending";

  const setControlsDisabled = (disabled: boolean): void => {
    use.disabled = disabled;
    discard.disabled = disabled;
  };

  const removeActionListeners = (): void => {
    use.removeEventListener("click", onUse);
    discard.removeEventListener("click", onDiscard);
  };

  const invoke = (
    callback: () => void,
    successMessage: string,
  ): void => {
    if (state !== "pending") return;
    state = "invoking";
    setControlsDisabled(true);

    try {
      callback();
    } catch {
      if (state === "invoking") {
        state = "pending";
        setControlsDisabled(false);
        status.textContent = "Annotation repair action failed. Repairs are pending; try again.";
      }
      return;
    }

    if (state !== "invoking") return;
    state = "decided";
    removeActionListeners();
    setControlsDisabled(true);
    status.textContent = successMessage;
  };

  function onUse(): void {
    invoke(() => actions.onUse(result.accepted), "Repaired annotations accepted.");
  }

  function onDiscard(): void {
    invoke(() => actions.onDiscard(retained), "Affected annotations discarded.");
  }

  use.addEventListener("click", onUse);
  discard.addEventListener("click", onDiscard);

  host.append(aside);
  const affectedCount = affectedIds.size;
  status.textContent = affectedCount === 1
    ? "1 annotation requires review. Repairs are pending."
    : `${affectedCount} annotations require review. Repairs are pending.`;
  aside.focus({ preventScroll: true });

  return Object.freeze({
    element: aside,
    destroy(): void {
      if (state === "destroyed") return;
      state = "destroyed";
      removeActionListeners();
      setControlsDisabled(true);
      aside.parentNode?.removeChild(aside);
    },
  });
};
